/**
 * Self-service password change.
 *
 * `POST /auth/password` requires the CURRENT password, so an access cookie on
 * its own is not enough to take permanent ownership of an account. The two
 * client-side checks below (confirmation matches, at least
 * `MIN_PASSWORD_LENGTH` characters) are courtesy only — the server enforces
 * the length itself with a 422 — but they turn two silent round trips into
 * instant feedback, and the confirmation check has no server equivalent at all.
 *
 * A successful change revokes every other session and re-issues this browser's
 * cookies on the same response, so the user stays signed in here and nowhere
 * else. Nothing in this component has to know that; it just must not treat the
 * response as a sign-out.
 */
import { useState } from 'react'
import type { FormEvent } from 'react'

import { ApiError, api } from '../api/client'
import { MIN_PASSWORD_LENGTH } from '../api/types'
import type { PasswordChangeResponse } from '../api/types'
import { Field, FormError, INPUT, SubmitButton } from '../auth/LoginPage'
import { useI18n } from '../i18n'
import type { TFunction } from '../i18n'

export interface ChangePasswordFormProps {
  /**
   * The password really changed server-side.
   *
   * Handed the server's own response rather than a bare signal:
   * `must_change_password: false` is the authoritative word that the forced
   * rotation is over, and a page that instead depends only on a `/auth/me`
   * refetch keeps showing the red banner if that refetch fails for any
   * non-401 reason — with the success message underneath it.
   */
  onChanged?: (result: PasswordChangeResponse) => void
}

export function ChangePasswordForm({ onChanged }: ChangePasswordFormProps) {
  const { t } = useI18n()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [pending, setPending] = useState(false)

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    if (pending) return
    setDone(false)

    if (next.length < MIN_PASSWORD_LENGTH) {
      setError(t('account.changePassword.tooShort', { min: MIN_PASSWORD_LENGTH }))
      return
    }
    if (next !== confirm) {
      setError(t('account.changePassword.mismatch'))
      return
    }

    setPending(true)
    setError(null)
    try {
      const result = await api.post<PasswordChangeResponse>('/auth/password', {
        current_password: current,
        new_password: next,
      })
      // Nothing typed here is worth keeping around in a DOM node once the
      // change has landed.
      setCurrent('')
      setNext('')
      setConfirm('')
      setDone(true)
      onChanged?.(result)
    } catch (err) {
      setError(describePasswordError(err, t))
    } finally {
      setPending(false)
    }
  }

  return (
    <section className="rounded-lg border border-border bg-surface p-6 shadow-sm">
      <form onSubmit={submit} noValidate className="space-y-4">
        <header className="space-y-1">
          <h2 className="text-lg font-semibold text-fg">{t('account.changePassword.title')}</h2>
          <p className="text-xs text-muted">{t('account.changePassword.note')}</p>
        </header>

        <Field id="current-password" label={t('account.changePassword.currentLabel')}>
          <input
            id="current-password"
            name="current_password"
            type="password"
            autoComplete="current-password"
            required
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className={INPUT}
          />
        </Field>

        <Field
          id="new-password"
          label={t('account.changePassword.newLabel')}
          hint={t('account.changePassword.newHint', { min: MIN_PASSWORD_LENGTH })}
        >
          <input
            id="new-password"
            name="new_password"
            type="password"
            autoComplete="new-password"
            required
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className={INPUT}
          />
        </Field>

        <Field id="confirm-password" label={t('account.changePassword.confirmLabel')}>
          <input
            id="confirm-password"
            name="confirm_password"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={INPUT}
          />
        </Field>

        <FormError message={error} />

        {done && (
          <p role="status" className="rounded-md bg-healthy/10 px-3 py-2 text-sm text-healthy">
            {t('account.changePassword.success')}
          </p>
        )}

        <SubmitButton pending={pending}>{t('account.changePassword.submit')}</SubmitButton>
      </form>
    </section>
  )
}

/**
 * `POST /auth/password` answers 400 — not 401 — for a wrong current password,
 * precisely so the API client's silent refresh-and-retry stays out of the way
 * and the real reason survives to here.
 *
 * `t` is passed in rather than read from a hook: this is a plain function, and
 * the string it returns is put straight into state, so it is translated in the
 * language that was active when the request failed.
 */
function describePasswordError(err: unknown, t: TFunction): string {
  if (err instanceof ApiError) {
    if (err.status === 400) return t('account.changePassword.errorWrongCurrent')
    if (err.status === 422) {
      return t('account.changePassword.tooShort', { min: MIN_PASSWORD_LENGTH })
    }
    if (err.status === 401) return t('account.changePassword.errorSessionExpired')
    return t('account.changePassword.errorStatus', { status: err.status })
  }
  return t('account.changePassword.errorNetwork')
}
